import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Paperclip } from 'lucide-react';
import { toast } from 'sonner';
import { listActiveProveedores } from '@/shared/api/controllers/proveedores';
import {
  ETAPAS_PLANTILLA_QUERY_KEY,
  listEtapasPlantilla,
} from '@/shared/api/controllers/importacionEtapasPlantilla';
import {
  crearImportacion,
  editarImportacion,
  getSiguienteNumeroImportacion,
  type Importacion,
  type MonedaImportacion,
} from '@/shared/api/controllers/importaciones';
import { uploadDocument } from '@/shared/api/documents';
import { ACCEPT_MAP, errorDeArchivo, MAX_UPLOAD_MB } from '@/shared/components/file-upload/uploadLimits';
import { Button } from '@/shared/components/ui/button';
import { DatePicker } from '@/shared/components/ui/date-picker';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import { hoyAr } from '@/features/facturacion/plazoNc';
import { parseMoney } from '@/shared/lib/money';
import { MoneyInput } from '@/features/ventas/components/MoneyInput';
import { FilePicker } from '@/shared/components/file-upload/FilePicker';

const DIAS_FECHA_PROBABLE = 90;
const SIN_FABRICANTE = 'ninguno';

function sumarDias(iso: string, dias: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + dias);
  return d.toISOString().slice(0, 10);
}

/** Días de `desde` a `hasta` (yyyy-mm-dd); null si falta alguna. */
function diasEntre(desde: string, hasta: string | null): number | null {
  if (!desde || !hasta) return null;
  const n = (Date.parse(`${hasta}T00:00:00Z`) - Date.parse(`${desde}T00:00:00Z`)) / 86_400_000;
  return Number.isFinite(n) ? Math.round(n) : null;
}

/**
 * Alta y edición. El número arranca en el siguiente del país y se puede cambiar;
 * la fecha probable arranca en fecha + 90 días, acompaña a la fecha mientras no
 * se toque a mano, y en el alta se pueden juntar adjuntos que se suben al crear.
 */
export function ImportacionDialog({
  open,
  importacion,
  onClose,
  onCreada,
}: {
  open: boolean;
  /** null = alta. */
  importacion: Importacion | null;
  onClose: () => void;
  /** Alta: recibe el id de la importación nueva (para abrir su detalle). */
  onCreada?: (id: number) => void;
}) {
  const qc = useQueryClient();
  const { config: activeCountry } = useActiveCountry();
  const archivosRef = useRef<HTMLInputElement>(null);
  /** null = sin tocar: se muestra el sugerido y, en el alta, se deja que lo asigne el server. */
  const [numero, setNumero] = useState<string | null>(null);
  const [fechaProbableManual, setFechaProbableManual] = useState(false);
  const [archivos, setArchivos] = useState<File[]>([]);
  const [descripcion, setDescripcion] = useState('');
  const [fecha, setFecha] = useState('');
  const [fechaProbable, setFechaProbable] = useState('');
  const [fabricanteId, setFabricanteId] = useState(SIN_FABRICANTE);
  const [moneda, setMoneda] = useState<MonedaImportacion>('USD');
  const [totalFob, setTotalFob] = useState('');
  const [error, setError] = useState<string | null>(null);

  const proveedoresQ = useQuery({
    queryKey: ['proveedores', 'activos'],
    queryFn: listActiveProveedores,
    enabled: open,
    staleTime: 5 * 60_000,
  });

  // En el alta, la plantilla dice si la fecha probable va a salir de las etapas.
  const plantillaQ = useQuery({
    queryKey: ETAPAS_PLANTILLA_QUERY_KEY,
    queryFn: listEtapasPlantilla,
    enabled: open && !importacion,
    staleTime: 60_000,
  });

  const siguienteQ = useQuery({
    queryKey: ['importaciones', activeCountry.code, 'siguiente-numero'],
    queryFn: getSiguienteNumeroImportacion,
    enabled: open && !importacion,
    staleTime: 0,
  });

  useEffect(() => {
    if (!open) return;
    const hoy = hoyAr();
    setNumero(importacion ? String(importacion.numero) : null);
    setArchivos([]);
    setDescripcion(importacion?.descripcion ?? '');
    setFecha(importacion?.fecha ?? hoy);
    setFechaProbable(importacion?.fechaProbable ?? sumarDias(hoy, DIAS_FECHA_PROBABLE));
    // Al editar sigue a la fecha sólo si estaba en fecha + 90.
    setFechaProbableManual(
      importacion != null && importacion.fechaProbable !== sumarDias(importacion.fecha, DIAS_FECHA_PROBABLE),
    );
    setFabricanteId(importacion?.fabricanteProveedorId ?? SIN_FABRICANTE);
    setMoneda(importacion?.moneda ?? 'USD');
    setTotalFob(importacion?.totalFob != null ? String(importacion.totalFob).replace('.', ',') : '');
    setError(null);
  }, [open, importacion]);

  // Sólo los marcados en el ABM; el que ya tiene la importación queda en la lista.
  const fabricantes = (proveedoresQ.data ?? []).filter(
    (p) => p.fields.EsFabricante_P === 'SI' || p.id === importacion?.fabricanteProveedorId,
  );

  // Con una etapa arribo la fecha probable no se carga: sale de las etapas. En
  // el alta se muestra lo que daría la plantilla (fecha + días hasta el arribo);
  // al editar, la guardada, que el server recalcula al guardar.
  const etapasActivas = (plantillaQ.data ?? [])
    .filter((e) => e.status === 'Activo')
    .sort((a, b) => a.orden - b.orden || Number(a.id) - Number(b.id));
  const iArribo = etapasActivas.findIndex((e) => e.esArribo);
  const arribo = importacion
    ? importacion.etapaArribo
    : iArribo >= 0
      ? etapasActivas[iArribo].nombre
      : null;
  const fechaProbableMostrada =
    !importacion && arribo != null && fecha !== ''
      ? sumarDias(fecha, etapasActivas.slice(0, iArribo + 1).reduce((s, e) => s + e.dias, 0))
      : fechaProbable;

  // Vacío = sin definir; se puede completar después.
  const fobVacio = totalFob.trim() === '';
  const fob = fobVacio ? null : parseMoney(totalFob);
  const fobValido = fobVacio || (fob != null && fob >= 0);

  // Número: al editar no puede quedar vacío; en el alta vacío = que lo asigne el server.
  const numeroMostrado = numero ?? (siguienteQ.data != null ? String(siguienteQ.data) : '');
  const numeroTexto = numeroMostrado.trim();
  const numeroCargado = numeroTexto === '' ? null : /^\d{1,9}$/.test(numeroTexto) ? Number(numeroTexto) : NaN;
  const numeroOk = numeroCargado === null ? !importacion : numeroCargado > 0;
  const valido =
    descripcion.trim() !== '' && fecha !== '' && fechaProbableMostrada !== '' && fobValido && numeroOk;

  const agregarArchivos = (lista: FileList | null) => {
    if (!lista) return;
    const nuevos: File[] = [];
    for (const f of Array.from(lista)) {
      const motivo = errorDeArchivo(f);
      if (motivo) toast.error('No se pudo adjuntar el archivo', { description: motivo });
      else if (!archivos.some((a) => a.name === f.name && a.size === f.size)) nuevos.push(f);
    }
    if (nuevos.length > 0) setArchivos((prev) => [...prev, ...nuevos]);
    if (archivosRef.current) archivosRef.current.value = '';
  };

  const mutation = useMutation({
    mutationFn: async () => {
      const body = {
        // Sin tocar en el alta: lo asigna el server (no choca con uno recién creado).
        numero: importacion || numero != null ? (numeroCargado ?? undefined) : undefined,
        descripcion: descripcion.trim(),
        fecha,
        fechaProbable: fechaProbableMostrada,
        fabricanteProveedorId: fabricanteId === SIN_FABRICANTE ? null : fabricanteId,
        moneda,
        totalFob: fob,
      };
      if (importacion) {
        await editarImportacion(importacion.id, body);
        return { id: null, fallidos: [] as string[] };
      }
      const { id } = await crearImportacion(body);
      // La importación ya existe: si un archivo falla no se deshace, se avisa.
      const fallidos: string[] = [];
      for (const archivo of archivos) {
        try {
          await uploadDocument({ entityType: 'Importacion', entityId: String(id), file: archivo });
        } catch {
          fallidos.push(archivo.name);
        }
      }
      return { id, fallidos };
    },
    onSuccess: ({ id, fallidos }) => {
      qc.invalidateQueries({ queryKey: ['importaciones'] });
      if (fallidos.length > 0) {
        toast.warning(
          `La importación se creó, pero no se pudo subir ${fallidos.length === 1 ? 'un archivo' : `${fallidos.length} archivos`}`,
          { description: `${fallidos.join(', ')}. Adjuntalos desde el detalle de la importación.`, duration: 10_000 },
        );
      }
      onClose();
      if (id != null) onCreada?.(id);
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'No se pudo guardar la importación.'),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !mutation.isPending && onClose()}>
      <DialogContent className="max-w-2xl" processing={mutation.isPending && 'Guardando…'}>
        <DialogHeader>
          <DialogTitle>
            {importacion ? `Editar importación N° ${importacion.numero}` : 'Nueva importación'}
          </DialogTitle>
          <DialogDescription>Cada importación corresponde a un contenedor.</DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-3">
          {/* El N° es corto: va al lado de la descripción, no en una fila propia. */}
          <div className="grid gap-3 md:grid-cols-[9rem_1fr]">
            <div className="space-y-1">
              <Label htmlFor="imp-numero">N° de importación</Label>
              <Input
                id="imp-numero"
                variant="filled"
                inputMode="numeric"
                placeholder="Automático"
                value={numeroMostrado}
                onChange={(e) => setNumero(e.target.value.replace(/\D/g, ''))}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="imp-descripcion">Descripción</Label>
              <Input
                id="imp-descripcion"
                variant="filled"
                autoFocus
                placeholder="Compra de Equipamiento Primer Trimestre"
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
              />
            </div>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1">
              <Label>Fecha</Label>
              <DatePicker
                value={fecha}
                onChange={(v) => {
                  setFecha(v);
                  if (v && !fechaProbableManual) setFechaProbable(sumarDias(v, DIAS_FECHA_PROBABLE));
                }}
                className="w-full"
                ariaLabel="Fecha de la importación"
              />
            </div>
            <div className="space-y-1">
              <Label>Fecha probable de llegada</Label>
              <DatePicker
                value={fechaProbableMostrada}
                onChange={(v) => {
                  setFechaProbable(v);
                  setFechaProbableManual(true);
                }}
                className="w-full"
                ariaLabel="Fecha probable de llegada"
                disabled={arribo != null}
                clearable={false}
              />
              {arribo != null && (
                <p className="text-xs text-muted-foreground">
                  Calculada desde las etapas
                  {diasEntre(fecha, fechaProbableMostrada) != null &&
                    `: ${diasEntre(fecha, fechaProbableMostrada)} días desde la fecha`}
                  .
                </p>
              )}
            </div>
          </div>
          <div className="space-y-1">
            <Label>Fabricante</Label>
            <Select value={fabricanteId} onValueChange={setFabricanteId}>
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar fabricante" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={SIN_FABRICANTE}>Sin definir</SelectItem>
                {fabricantes.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.fields.Nombre_P}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {proveedoresQ.isSuccess && fabricantes.length === 0 && (
              <p className="text-xs text-muted-foreground">
                No hay fabricantes. Marcá "Es fabricante" en el proveedor, en Configuración → Proveedores.
              </p>
            )}
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1">
              <Label>Moneda</Label>
              <Select value={moneda} onValueChange={(v) => setMoneda(v as MonedaImportacion)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="ARS">ARS</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="imp-fob">Total FOB</Label>
              <MoneyInput
                id="imp-fob"
                variant="filled"
                placeholder="Sin definir"
                value={totalFob}
                moneda={moneda}
                onChange={setTotalFob}
              />
              <p className="text-xs text-muted-foreground">Sólo lo del fabricante; fletes y otras compras no entran. Se puede completar después.</p>
            </div>
          </div>
          {!importacion && (
            <div className="space-y-1.5">
              <Label>Adjuntos</Label>
              <input
                ref={archivosRef}
                type="file"
                multiple
                accept={ACCEPT_MAP['pdf-image'].accept}
                className="hidden"
                onChange={(e) => agregarArchivos(e.target.files)}
              />
              {/* Mismo renglón que el resto de los adjuntos antes de guardar: ojo para verlo y X para quitarlo. */}
              {archivos.length > 0 && (
                <div className="space-y-1.5">
                  {archivos.map((f, i) => (
                    <FilePicker
                      key={`${f.name}-${f.size}-${i}`}
                      file={f}
                      onChange={(n) => n == null && setArchivos((prev) => prev.filter((_, j) => j !== i))}
                    />
                  ))}
                </div>
              )}
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  onClick={() => archivosRef.current?.click()}
                  disabled={mutation.isPending}
                >
                  <Paperclip className="h-3.5 w-3.5" aria-hidden /> Agregar archivos
                </Button>
                <p className="text-xs text-muted-foreground">
                  Invoice, proforma u otros (PDF o imagen, hasta {MAX_UPLOAD_MB} MB cada uno). Se suben al crear.
                </p>
              </div>
            </div>
          )}
          {error && <p className="text-sm font-medium text-destructive">{error}</p>}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button onClick={() => mutation.mutate()} disabled={!valido || mutation.isPending}>
            {importacion ? 'Guardar cambios' : 'Crear importación'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
